import { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowLeft,
  Search,
  CheckCircle,
  XCircle,
  Clock,
  Eye,
} from 'lucide-react';
import { Card, CardContent } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Select } from '@/components/ui/Select';
import { PageLoader } from '@/components/ui/Spinner';
import { DataTable, type Column } from '@/components/ui/DataTable';
import { useReferrals, useValidateReferral, useRejectReferral } from '@/hooks/useReferrals';
import { STATUS_CONFIG, type Referral, type ReferralFilters, type ReferralStatus } from '@/types/referral.types';
import RejectReferralModal from './RejectReferralModal';

const statusOptions = [
  { value: 'all', label: 'Todos os status' },
  { value: 'pending', label: 'Pendentes' },
  { value: 'validated', label: 'Validadas' },
  { value: 'rejected', label: 'Rejeitadas' },
];

function formatDate(dateStr: string) {
  return new Date(dateStr).toLocaleDateString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function formatPhone(phone?: string) {
  if (!phone) return '-';
  if (phone.startsWith('55') && phone.length >= 12) {
    const country = phone.slice(0, 2);
    const area = phone.slice(2, 4);
    const part1 = phone.slice(4, 9);
    const part2 = phone.slice(9);
    return `+${country} ${area} ${part1}-${part2}`;
  }
  return `+${phone}`;
}

export default function ReferralsListPage() {
  const [filters, setFilters] = useState<ReferralFilters>({
    status: 'all',
    page: 1,
    limit: 20,
  });
  const [referrerSearch, setReferrerSearch] = useState('');
  const [referralToReject, setReferralToReject] = useState<Referral | null>(null);

  const { data, isLoading, isError } = useReferrals(filters);
  const validateMutation = useValidateReferral();
  const rejectMutation = useRejectReferral();

  const handleValidate = async (referral: Referral) => {
    await validateMutation.mutateAsync(referral.id);
  };

  const handleReject = async (reason: string) => {
    if (!referralToReject) return;
    await rejectMutation.mutateAsync({
      id: referralToReject.id,
      input: { reason },
    });
    setReferralToReject(null);
  };

  const handleFilterChange = (key: keyof ReferralFilters, value: string) => {
    setFilters((prev) => ({
      ...prev,
      [key]: value,
      page: 1,
    }));
  };

  const columns: Column<Referral>[] = [
    {
      key: 'referrer',
      label: 'Quem Indicou',
      render: (referral) => (
        <div>
          <p className="font-medium text-gray-900 dark:text-white">
            {referral.referrerName || 'Sem nome'}
          </p>
          <p className="text-sm text-gray-500 dark:text-gray-400">
            {formatPhone(referral.referrerPhone)}
          </p>
        </div>
      ),
    },
    {
      key: 'referee',
      label: 'Indicado',
      render: (referral) => (
        <div>
          <p className="font-medium text-gray-900 dark:text-white">
            {referral.refereeName || 'Sem nome'}
          </p>
          <p className="text-sm text-gray-500 dark:text-gray-400">
            {formatPhone(referral.refereePhone)}
          </p>
        </div>
      ),
    },
    {
      key: 'status',
      label: 'Status',
      render: (referral) => {
        const config = STATUS_CONFIG[referral.status];
        return (
          <span
            className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${config.bgColor} ${config.color}`}
          >
            {referral.status === 'pending' && <Clock className="w-3 h-3 mr-1" />}
            {referral.status === 'validated' && <CheckCircle className="w-3 h-3 mr-1" />}
            {referral.status === 'rejected' && <XCircle className="w-3 h-3 mr-1" />}
            {config.label}
          </span>
        );
      },
    },
    {
      key: 'reward',
      label: 'Reward',
      render: (referral) => (
        <div className="text-sm">
          {referral.premiumDaysAwarded && (
            <p className="text-green-600 dark:text-green-400">
              +{referral.premiumDaysAwarded} dias
            </p>
          )}
          {referral.creditsAwarded && (
            <p className="text-blue-600 dark:text-blue-400">
              R$ {referral.creditsAwarded}
            </p>
          )}
          {!referral.premiumDaysAwarded && !referral.creditsAwarded && (
            <span className="text-gray-400">-</span>
          )}
        </div>
      ),
    },
    {
      key: 'createdAt',
      label: 'Data',
      render: (referral) => (
        <span className="text-sm text-gray-500 dark:text-gray-400">
          {formatDate(referral.createdAt)}
        </span>
      ),
    },
    {
      key: 'actions',
      label: 'Acoes',
      render: (referral) => (
        <div className="flex items-center gap-2">
          <Link to={`/admin/referrals/${referral.id}`}>
            <Button variant="ghost" size="sm">
              <Eye className="w-4 h-4" />
            </Button>
          </Link>
          {referral.status === 'pending' && (
            <>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => handleValidate(referral)}
                disabled={validateMutation.isPending}
                className="text-green-600 hover:text-green-700 hover:bg-green-50 dark:text-green-400 dark:hover:bg-green-900/20"
              >
                <CheckCircle className="w-4 h-4" />
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setReferralToReject(referral)}
                className="text-red-600 hover:text-red-700 hover:bg-red-50 dark:text-red-400 dark:hover:bg-red-900/20"
              >
                <XCircle className="w-4 h-4" />
              </Button>
            </>
          )}
        </div>
      ),
    },
  ];

  if (isLoading) {
    return <PageLoader message="Carregando indicacoes..." />;
  }

  if (isError) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[400px]">
        <p className="text-red-500 dark:text-red-400">
          Erro ao carregar indicacoes
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Link to="/admin/referrals">
          <Button variant="ghost" size="sm">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Voltar
          </Button>
        </Link>
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
            Indicacoes
          </h1>
          <p className="text-gray-500 dark:text-gray-400">
            Gerencie todas as indicacoes do sistema
          </p>
        </div>
      </div>

      {/* Filters */}
      <Card>
        <CardContent>
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="flex-1">
              <Input
                placeholder="Buscar por telefone do referrer..."
                value={referrerSearch}
                onChange={(e) => setReferrerSearch(e.target.value)}
                leftIcon={<Search className="w-4 h-4" />}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    handleFilterChange('referrerId', referrerSearch);
                  }
                }}
              />
            </div>
            <div className="w-full sm:w-48">
              <Select
                value={filters.status || 'all'}
                onChange={(e) => handleFilterChange('status', e.target.value as ReferralStatus | 'all')}
                options={statusOptions}
              />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Table */}
      <DataTable
        data={data?.referrals || []}
        columns={columns}
        keyExtractor={(item) => item.id}
        emptyMessage="Nenhuma indicacao encontrada"
        pagination={{
          page: filters.page || 1,
          pageSize: filters.limit || 20,
          total: data?.total || 0,
          onPageChange: (page) => setFilters((prev) => ({ ...prev, page })),
        }}
      />

      {/* Reject Modal */}
      <RejectReferralModal
        isOpen={!!referralToReject}
        onClose={() => setReferralToReject(null)}
        onConfirm={handleReject}
        isLoading={rejectMutation.isPending}
        referral={referralToReject}
      />
    </div>
  );
}
